/*
 * cart-essentials.js: the cart helpers every page loads (layout/theme.liquid), whichever way the cart is
 * shown (settings.cart_experience: the /cart page, or the cart drawer that assets/hb-cart-drawer.js draws
 * on every other page). It stays loaded next to both, and is never swapped for another script.
 *
 *   - The header cart bubble refresh (window.updateCartUI), which also opens the drawer when asked to.
 *   - window.hbRenderedCartIsKnownEmpty, which countries-consolidate.js reads: without it the market check
 *     sends its clear even for an empty cart, as it did before the check.
 *   - The checkout button for the cart page and the drawer, on ONE delegated click listener. Since Nov 2026
 *     only a free GIFT still to choose holds the tap back (the drawer's Free gifts & samples panel; a modal
 *     on /cart); a missing sample goes straight to checkout, which adds one (and a gift the shopper skips).
 *     window.hbCartCheckout exposes the gate to the drawer script.
 *   - The window.waitForGiftCartIfBusy fallback for pages that load no cart.js.
 * assets/cart.js holds the cart's quantity, promo and gift logic. It loads on the cart template and, in
 * drawer mode, when the drawer first opens.
 */

window.__dkCartSilent = false;

function hasCartChanged(prevCart, nextCart) {
  if (!prevCart || !nextCart) return false;
  if (prevCart.item_count !== nextCart.item_count) return true;

  const prevItems = (prevCart.items || []).map((i) => `${i.variant_id}:${i.quantity}`);
  const nextItems = (nextCart.items || []).map((i) => `${i.variant_id}:${i.quantity}`);

  if (prevItems.length !== nextItems.length) return true;
  for (let idx = 0; idx < prevItems.length; idx += 1) {
    if (prevItems[idx] !== nextItems[idx]) return true;
  }
  return false;
}
window.hasCartChanged = window.hasCartChanged || hasCartChanged;

var hbCartUIRefreshState = window.__hbCartUIRefreshState || {
  controllers: new Set(),
  leaving: false,
};
if (!window.__hbCartUIRefreshState) {
  window.__hbCartUIRefreshState = hbCartUIRefreshState;
  window.addEventListener("pagehide", () => {
    hbCartUIRefreshState.leaving = true;
    hbCartUIRefreshState.controllers.forEach((controller) => controller.abort());
  });
  window.addEventListener("pageshow", () => {
    hbCartUIRefreshState.leaving = false;
  });
}

async function readCartUIRefresh(root, signal) {
  const sectionUrl = new URL(window.location.href);
  sectionUrl.searchParams.set("sections", "cart-icon-bubble");
  const [cartResponse, sectionResponse] = await Promise.all([
    (window.hbCartFetchWithTimeout || fetch)(`${root}cart.js?dk_silent=1`, { signal, headers: { Accept: "application/json", "X-DK-Silent": "1" } }),
    (window.hbCartFetchWithTimeout || fetch)(sectionUrl.href, { signal, headers: { Accept: "application/json" } }),
  ]);
  if (!cartResponse.ok || !sectionResponse.ok) {
    throw new Error(`Cart refresh recovery failed: HTTP ${cartResponse.status}/${sectionResponse.status}`);
  }
  const [cart, sections] = await Promise.all([cartResponse.json(), sectionResponse.json()]);
  return { ...cart, sections };
}

function hbHasCartCookie() {
  try {
    return /(?:^|;\s*)cart=[^;]/.test(document.cookie);
  } catch (_) {
    return true;
  }
}

window.updateCartUI = async function updateCartUI(willOpen = false, baselineCart = null) {
  if (hbCartUIRefreshState.leaving) return null;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  const url = `${root}cart/update.js?dk_silent=1`;
  const controller = new AbortController();
  hbCartUIRefreshState.controllers.add(controller);
  // With HB_CART_ON_FIRST_ADD_ONLY on and no cart cookie there is no cart to refresh, and the POST below
  // would create an empty one (a Back/Forward restore, a back_forward load and a navigation served from
  // Shopify's prefetch rules all refresh the bubble). The two GETs read the same bubble without creating a
  // cart. Read when the refresh runs, so a cart created on this page since load takes the POST as before.
  const noCartYet = HB_CART_ON_FIRST_ADD_ONLY && !hbHasCartCookie();

  try {
    window.__dkCartSilent = true;

    let res;
    let parsedState;
    try {
      if (noCartYet) {
        parsedState = await readCartUIRefresh(root, controller.signal);
      } else {
        res = await (window.hbCartFetchWithTimeout || fetch)(url, {
          signal: controller.signal,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-DK-Silent": "1",
          },
          body: JSON.stringify({
            updates: {},
            sections: ["cart-icon-bubble"],
            sections_url: window.location.pathname,
          }),
        });
      }
    } catch (error) {
      if (controller.signal.aborted || hbCartUIRefreshState.leaving || error.name === "AbortError") return null;
      if (!noCartYet && error instanceof TypeError && navigator.onLine !== false) {
        parsedState = await readCartUIRefresh(root, controller.signal);
      } else {
        throw error;
      }
    }

    // A 429 or an error page answers with HTML, which res.json() failed on ("Unexpected token <") and
    // logged as an error. Anything but a cart is a skipped refresh: the bubble keeps its count and callers
    // get null, as for a failed request. The body decides, not the content type: Shopify serves the cart
    // as text/javascript to a request that does not ask for JSON.
    if (res) parsedState = res.ok ? await res.json().catch(() => null) : null;
    if (controller.signal.aborted || hbCartUIRefreshState.leaving) return null;
    if (!parsedState) {
      console.warn(`Cart bubble refresh skipped: HTTP ${res?.status || "unavailable"}`);
      return null;
    }

    const cartIconHtml = parsedState.sections?.["cart-icon-bubble"];
    if (cartIconHtml) {
      const iconDoc = new DOMParser().parseFromString(cartIconHtml, "text/html");
      const targetIcon = document.getElementById("cart-icon-bubble");
      const sourceIcon = iconDoc.querySelector(".shopify-section");
      if (targetIcon && sourceIcon) {
        targetIcon.innerHTML = sourceIcon.innerHTML;
        hbCartUIRefreshState.bubbleRefreshed = true;
      }
    }

    // Callers that ask for the cart to show (willOpen) get the drawer, once the bubble is current. The cart
    // page has no drawer, and nothing else opens.
    if (willOpen && window.hbCartDrawer?.open) {
      try {
        window.hbCartDrawer.open({ reason: "api" });
      } catch (openError) {
        console.error("Cart drawer open failed:", openError);
      }
    }

    return parsedState;
  } catch (error) {
    if (controller.signal.aborted || hbCartUIRefreshState.leaving || error.name === "AbortError") return null;
    console.error("Error updating cart:", error);
    return null;
  } finally {
    hbCartUIRefreshState.controllers.delete(controller);
    setTimeout(() => {
      if (hbCartUIRefreshState.controllers.size === 0) window.__dkCartSilent = false;
    }, 80);
  }
};

window.refreshCartUI = window.updateCartUI;

/*
 * HB_CART_ON_FIRST_ADD_ONLY: no cart before the first add to bag (Oct 2026, needs fraud-team sign-off).
 * Any cookie-less POST to /cart/clear.js or /cart/update.js creates a cart and sets the `cart` cookie. The
 * page-load market clear (countries-consolidate.js) and the idle bubble refresh below sent one on every first
 * page view, so every visitor had a cart from page 1, and Shopify renders every page for a shopper with a cart
 * uncached: live, Oct 2026, an empty cart cookie took server processing from 145-265 ms to 550-612 ms plus
 * ~300-350 ms edge_cart, and TTFB up by ~390-500 ms (PLP 937 -> 1,389 ms, PDP 1,026 -> 1,523 ms, home 934 ->
 * 1,324 ms, medians), on every page view after the first, for shoppers who never add to bag too.
 * true: a fresh network render of an empty cart counts as known empty with or without a cart cookie, so those
 *   two requests are not sent; the first cart comes from the shopper's first add (or other cart request), and
 *   the Signifyd release in layout/theme.liquid already waits up to 3 s for that cookie on purchase intent.
 * false: the cookie is required again and the page-1 cart comes back exactly as before (clear, then refresh).
 */
const HB_CART_ON_FIRST_ADD_ONLY = true;

// True while the switch is on and this browser has no cart yet: a request that would only clear or refresh
// an empty cart is skipped instead of creating one. countries-consolidate.js reads it before a market change.
window.hbNoCartYet = function hbNoCartYet() {
  return HB_CART_ON_FIRST_ADD_ONLY && !hbHasCartCookie();
};

/*
 * True only for a fresh network render of an empty cart (window.hbInitialCartItemCount, layout/theme.liquid).
 * History (HTTP cache), prefetched and prerendered documents can carry an old count, so they return false.
 * With HB_CART_ON_FIRST_ADD_ONLY off it also needs a cart cookie: without one, the first cart request on the
 * page (the bubble refresh below, or the market clear in countries-consolidate.js) creates the cart, and both
 * callers then send it so a first visitor gets a cart on page 1.
 * Shared with countries-consolidate.js, which calls it from its DOMContentLoaded listener: both files are
 * deferred, so this top-level assignment has run by then although that file loads first.
 */
window.hbRenderedCartIsKnownEmpty = function hbRenderedCartIsKnownEmpty() {
  // With the switch on, no cart cookie means no cart at all, so it is empty whatever kind of navigation
  // drew this page: a prefetched or history document (Back, Chrome's prefetch of a search result or a
  // Shopify speculation rule) would otherwise send the market clear or the bubble refresh and create one.
  if (HB_CART_ON_FIRST_ADD_ONLY && !hbHasCartCookie()) return true;
  if (!HB_CART_ON_FIRST_ADD_ONLY) {
    try {
      if (!/(?:^|;\s*)cart=[^;]/.test(document.cookie)) return false;
    } catch (_) {
      return false;
    }
  }
  return window.hbRenderedCartWasEmpty();
};

/*
 * The same check without the cart cookie: a fresh network render of an empty cart, so the header bubble the
 * server drew with this page is current until the cart changes on it. countries-consolidate.js also reads it
 * to skip the bubble refresh after a market clear (the clear only runs on such a page with
 * HB_CART_ON_FIRST_ADD_ONLY off, where it creates a first visitor's cart).
 */
window.hbRenderedCartWasEmpty = function hbRenderedCartWasEmpty() {
  if (Number(window.hbInitialCartItemCount) !== 0) return false;
  return window.hbRenderedPageIsFresh();
};

/*
 * True for a fresh network render of this document: Shopify drew its header bubble from the cart as it was a
 * moment ago (a page for a shopper with a cart is never served from Shopify's page cache). History (HTTP cache),
 * prefetched and prerendered documents can carry an old count.
 */
window.hbRenderedPageIsFresh = function hbRenderedPageIsFresh() {
  try {
    const nav = performance.getEntriesByType("navigation")[0];
    return (
      Boolean(nav) &&
      (nav.type === "navigate" || nav.type === "reload") &&
      !nav.deliveryType &&
      !(nav.activationStart > 0) &&
      !document.prerendering
    );
  } catch (_) {
    return false;
  }
};

/*
 * Redraws the header bag icon with one GET (the cart-icon-bubble section of this page): no cart write, so no
 * cart is created and no cart attribute is saved back. Resolves true when the bubble was redrawn.
 */
window.hbRefreshCartBubble = async function hbRefreshCartBubble() {
  if (hbCartUIRefreshState.leaving) return false;
  const doFetch = window.hbCartFetchWithTimeout || fetch;
  try {
    const res = await doFetch(`${window.location.pathname}?sections=cart-icon-bubble`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    const sections = res.ok ? await res.json().catch(() => null) : null;
    const html = sections && sections["cart-icon-bubble"];
    const targetIcon = document.getElementById("cart-icon-bubble");
    if (!html || !targetIcon) return false;
    const sourceIcon = new DOMParser().parseFromString(html, "text/html").querySelector(".shopify-section");
    if (!sourceIcon) return false;
    targetIcon.innerHTML = sourceIcon.innerHTML;
    hbCartUIRefreshState.bubbleRefreshed = true;
    return true;
  } catch (error) {
    if (!hbCartUIRefreshState.leaving) console.warn("Cart bubble refresh skipped:", error);
    return false;
  }
};

function scheduleInitialCartUIRefresh() {
  /*
   * The page-view bubble refresh. It used to POST cart/update.js ({updates: {}}) on every page view with a bag,
   * a cart write per page view whose only use was the bag icon (and which could save an old cart attribute
   * back, see hb-hazmat-volume.js). Now nothing is sent when the server's bubble is current: a fresh network
   * render (window.hbRenderedPageIsFresh), or no cart at all with HB_CART_ON_FIRST_ADD_ONLY on. A history,
   * prefetched or prerendered document, or a cart change published before the refresh is due, redraws the
   * bubble with one GET (window.hbRefreshCartBubble).
   */
  let cartChanged = false;
  let stopWatchingCart = null;
  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartUpdate) {
    stopWatchingCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      cartChanged = true;
    });
  }

  const refresh = async () => {
    // The page-load market check (countries-consolidate.js) goes first. On a fast load the idle period can come
    // while its /cart/clear.js is still in flight; a refresh sent then carries no cart cookie either, so a first
    // visitor's cart could be created twice, by both requests, with the later token winning. Capped at 8 s, as
    // in cart.js and hb-hazmat-volume.js.
    let marketTimer;
    try {
      if (typeof window.hbWhenCartMarketSettled === "function") {
        await Promise.race([
          window.hbWhenCartMarketSettled(),
          new Promise((resolve) => (marketTimer = setTimeout(resolve, 8000))),
        ]);
      }
    } catch (_) {
    } finally {
      clearTimeout(marketTimer);
    }
    if (stopWatchingCart) stopWatchingCart();
    // A refresh already redrew the bubble on this page (the one countries-consolidate.js sends after a market
    // clear that emptied lines), so this repeat of a request that already went out is skipped.
    if (hbCartUIRefreshState.bubbleRefreshed) return;
    // Checked when the refresh is due rather than at DOMContentLoaded. With HB_CART_ON_FIRST_ADD_ONLY on, an
    // empty bag the server drew stays known empty with or without a cookie, so no cart is created here. With it
    // off, a first visit's market clear creates the cart cookie in between (the empty bubble is still current),
    // and without a cookie by now the refresh goes out and creates the cart, as before.
    if (stopWatchingCart && !cartChanged && (window.hbRenderedCartIsKnownEmpty() || window.hbRenderedPageIsFresh())) {
      return;
    }
    window.hbRefreshCartBubble();
  };

  if (document.readyState !== "complete") {
    window.addEventListener(
      "load",
      () => {
        if ("requestIdleCallback" in window) {
          window.requestIdleCallback(refresh, { timeout: 3000 });
        } else {
          setTimeout(refresh, 1200);
        }
      },
      { once: true },
    );
    return;
  }

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(refresh, { timeout: 3000 });
  } else {
    setTimeout(refresh, 1200);
  }
}

document.addEventListener("DOMContentLoaded", () => {
  scheduleInitialCartUIRefresh();

  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // The drawer drew this change from the response that made it, bubble included.
      if (event?.surfaceRendered === true) return;
      if (
        event.source === "cart-items" ||
        event.source === "product-form" ||
        event.source === "product-form-post-silent" ||
        event.source === "pdp-cross-sell"
      ) {
        return;
      }
      window.updateCartUI(false);
    });
  }
});

(() => {
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    cartFreebiesCheckoutSubmitting = false;
    closeFreebiesModal({ restoreFocus: false });
    clearCartCheckoutMessage();
    initializeCartCheckoutButtons();
    // A Back/Forward restore shows the bubble as it was when the page was left: one GET redraws it (no cart write).
    window.hbRefreshCartBubble();

    document.querySelectorAll("[data-cart-checkout-button]").forEach((btn) => {
      if (!btn.classList.contains("cart-checkout-button--loading")) return;
      btn.classList.remove("cart-checkout-button--loading", "cart-checkout-button--locked");
      btn.removeAttribute("disabled");
      btn.removeAttribute("aria-disabled");
      if (btn.dataset.originalHtml) {
        btn.innerHTML = btn.dataset.originalHtml;
        delete btn.dataset.originalHtml;
      }
    });
  }
});

let cartFreebiesModalCheckoutButton = null;
let cartFreebiesCheckoutSubmitting = false;
let cartFreebiesBodyHadOverflowHidden = false;

function parseFreebiesNumber(value) {
  const parsedValue = Number.parseInt(value, 10);
  return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : null;
}

function normalizeFreebiesCount(selected, allowed) {
  const normalizedAllowed = parseFreebiesNumber(allowed);
  if (!normalizedAllowed) return { selected: 0, allowed: 0 };

  return {
    selected: parseFreebiesNumber(selected) ?? 0,
    allowed: normalizedAllowed,
  };
}

function parseFreebiesCountText(text) {
  // Arabic numbers (theme setting): the counter may be in Arabic digits.
  const match = (window.hbNumerals ? window.hbNumerals.toLatin(text || "") : String(text || "")).match(/(\d+)\s*\/\s*(\d+)/);
  if (!match) return { selected: 0, allowed: 0 };

  return normalizeFreebiesCount(match[1], match[2]);
}

function getFreebiesPanel(type) {
  const scope = document.getElementById("CartDrawer") || document;
  return Array.from(scope.querySelectorAll("[data-cart-picker-root]")).filter((picker) => !picker.hidden)
    .map((picker) => picker.querySelector(`[data-tab-panel="${type}"]`))
    .find((panel) => panel && !panel.hidden) || null;
}

function getFreebiesCountFromPanel(type) {
  const panel = getFreebiesPanel(type);
  if (!panel) {
    return { selected: 0, allowed: 0 };
  }

  const statusElement =
    panel.querySelector(`[data-freebies-type="${type}"]`) ||
    panel.querySelector(".cart-page-picker__status");

  if (statusElement?.dataset.selectedCount !== undefined && statusElement?.dataset.allowedCount !== undefined) {
    return normalizeFreebiesCount(statusElement.dataset.selectedCount, statusElement.dataset.allowedCount);
  }

  const counterElement =
    panel.querySelector(`[data-freebies-counter="${type}"]`) ||
    panel.querySelector(".cart-page-picker__status-counter");

  return parseFreebiesCountText(counterElement?.textContent || statusElement?.textContent);
}

function getFreebiesSelectionState() {
  return {
    samples: getFreebiesCountFromPanel("samples"),
    gifts: getFreebiesCountFromPanel("gifts"),
  };
}

function isDisplayed(element) {
  if (!element) return false;
  if (element.hidden || element.classList.contains("hide")) return false;
  const inlineHidden = (element.getAttribute("style") || "").replace(/\s/g, "").toLowerCase().includes("display:none");
  if (inlineHidden) return false;
  return window.getComputedStyle(element).display !== "none";
}

function isAddableFreebieCard(card) {
  if (!card) return false;
  if (card.querySelector("[data-cart-page-picker-remove]")) return false;
  if (card.dataset.giftLocked === "true" || card.dataset.thresholdLocked === "true") return false;

  // Notify Me / sold-out is the active CTA — not selectable
  const unavailableCta = card.querySelector(".notify-me-btn, .out-of-stock-btn, .back-in-stock-btn");
  if (unavailableCta) {
    const unavailableRoot =
      unavailableCta.closest(
        ".waitlist-container, .back-in-stock-container, .bundle-waitlist-container, .bundle-back-in-stock-container",
      ) || unavailableCta;
    if (isDisplayed(unavailableRoot)) return false;
  }

  const activeButtons = card.querySelector(".product-form__buttons:not(.hide)");
  if (!activeButtons) return false;
  if (!isDisplayed(activeButtons.closest(".buy-buttons-container") || activeButtons)) return false;

  const submit = activeButtons.querySelector(
    "button.product-form__submit:not([disabled]):not([data-threshold-lock]):not([data-cart-page-picker-remove])",
  );
  return Boolean(submit && submit.getAttribute("aria-disabled") !== "true" && !submit.hasAttribute("hidden"));
}

function panelHasAddableFreebie(type) {
  const panel = getFreebiesPanel(type);
  if (!panel) return false;

  return Array.from(panel.querySelectorAll(".suggested-items__product-card__wrapper")).some(isAddableFreebieCard);
}

function hasUnselectedFreebies(state = getFreebiesSelectionState()) {
  if (document.getElementById("CartDrawer")) return getMissingFreebieTypes(state).length > 0;
  return Object.entries(state).some(([type, { selected, allowed }]) => {
    return allowed > 0 && selected < allowed && panelHasAddableFreebie(type);
  });
}

// One reminder for each kind the shopper has not chosen, with gifts first when both are missing.
function getMissingFreebieTypes(state = getFreebiesSelectionState()) {
  return ["gifts", "samples"].filter((type) => {
    const { selected, allowed } = state[type];
    return allowed > 0 && selected === 0 && panelHasAddableFreebie(type);
  });
}

// The checkout reminder is for gifts only (Nov 2026): a gift slot with none chosen and a gift card to pick. A missing
// sample never holds checkout back; checkout adds the first sample of the market (hb-shopify-applications,
// Sample selector autoFreebies.mjs), and a gift the shopper skips past the reminder too.
function hasUnselectedGift(state = getFreebiesSelectionState()) {
  return getMissingFreebieTypes(state).includes("gifts");
}

// The first type ("samples" or "gifts") that still has a free slot and a card to fill it, in the order the
// picker lists its accordions (show_gifts_first), or null when everything allowed is chosen. The drawer opens
// its Free gifts & samples panel on it.
function firstUnselectedType(state = getFreebiesSelectionState()) {
  if (document.getElementById("CartDrawer")) return getMissingFreebieTypes(state)[0] || null;
  const picker = document.querySelector("[data-cart-picker-root]");
  const order = Array.from(picker?.querySelectorAll("[data-tab-panel]") || [], (panel) => panel.dataset.tabPanel).filter(
    (type) => type === "samples" || type === "gifts",
  );
  ["samples", "gifts"].forEach((type) => {
    if (!order.includes(type)) order.push(type);
  });

  return (
    order.find((type) => {
      const { selected, allowed } = state[type] || { selected: 0, allowed: 0 };
      return allowed > 0 && selected < allowed && panelHasAddableFreebie(type);
    }) || null
  );
}

// The drawer draws no freebies modal (its Free gifts & samples panel replaces it), so a modal node inside
// #CartDrawer is never the one to open, move or initialize.
function getFreebiesModal() {
  const modals = Array.from(document.querySelectorAll("[data-cart-freebies-modal]")).filter(
    (modal) => !modal.closest("#CartDrawer"),
  );
  if (!modals.length) return null;

  const embeddedModal = modals.find((modal) => modal.parentElement !== document.body);
  return embeddedModal || modals[0];
}

function cleanupDuplicateFreebiesModals() {
  const modal = getFreebiesModal();
  if (!modal) return;

  document.querySelectorAll("[data-cart-freebies-modal]").forEach((otherModal) => {
    if (otherModal !== modal && otherModal.hidden) otherModal.remove();
  });
}

function cleanupStaleFreebiesModalState() {
  const modal = getFreebiesModal();
  if (!document.body.classList.contains("cart-freebies-modal-open") || (modal && !modal.hidden)) return;

  document.body.classList.remove("cart-freebies-modal-open");
  if (!cartFreebiesBodyHadOverflowHidden) {
    document.body.classList.remove("overflow-hidden");
  }
  document.removeEventListener("keydown", handleFreebiesModalKeydown);
  cartFreebiesModalCheckoutButton = null;
}

function getFreebiesSkipButton() {
  return getFreebiesModal()?.querySelector("[data-cart-freebies-skip]");
}

function setFreebiesSkipButtonLabel() {
  const modal = getFreebiesModal();
  const skipButton = getFreebiesSkipButton();
  if (!modal || !skipButton) return;

  if (!skipButton.dataset.defaultLabel) {
    skipButton.dataset.defaultLabel =
      modal.dataset.skipLabelCheckout || skipButton.textContent.trim();
  }

  skipButton.textContent = modal.dataset.skipLabelCheckout || skipButton.dataset.defaultLabel;
}

function openFreebiesModal(triggerButton) {
  const modal = getFreebiesModal();
  if (!modal) {
    continueToCheckout(triggerButton);
    return;
  }

  initializeFreebiesModal();
  cartFreebiesModalCheckoutButton = triggerButton;
  cartFreebiesBodyHadOverflowHidden = document.body.classList.contains("overflow-hidden");
  setFreebiesSkipButtonLabel();

  modal.hidden = false;
  modal.removeAttribute("aria-hidden");
  document.body.classList.add("overflow-hidden", "cart-freebies-modal-open");
  document.addEventListener("keydown", handleFreebiesModalKeydown);

  const dialog = modal.querySelector('[role="dialog"]');
  const initialFocus = dialog?.querySelector("button, [href], input, select, textarea") || dialog;

  requestAnimationFrame(() => {
    if (typeof trapFocus === "function" && dialog) {
      trapFocus(dialog, initialFocus);
      return;
    }
    initialFocus?.focus();
  });
}

function closeFreebiesModal({ restoreFocus = true } = {}) {
  const modal = getFreebiesModal();
  if (!modal || modal.hidden) return;

  modal.hidden = true;
  modal.setAttribute("aria-hidden", "true");
  document.body.classList.remove("cart-freebies-modal-open");
  if (!cartFreebiesBodyHadOverflowHidden) {
    document.body.classList.remove("overflow-hidden");
  }
  document.removeEventListener("keydown", handleFreebiesModalKeydown);

  const focusTarget =
    restoreFocus && cartFreebiesModalCheckoutButton?.isConnected ? cartFreebiesModalCheckoutButton : null;

  if (typeof removeTrapFocus === "function") {
    removeTrapFocus(focusTarget);
  } else if (focusTarget) {
    focusTarget.focus();
  }

  cartFreebiesModalCheckoutButton = null;
  setFreebiesSkipButtonLabel();
}

function getStickyHeaderOffset() {
  const rootStyle = window.getComputedStyle(document.documentElement);
  const cssHeaderHeight = Number.parseFloat(rootStyle.getPropertyValue("--header-height")) || 0;
  const header = document.querySelector(".section-header");
  const headerRect = header?.getBoundingClientRect();
  const visibleHeaderHeight = headerRect && headerRect.top <= 0 && headerRect.bottom > 0 ? headerRect.bottom : 0;

  return Math.max(cssHeaderHeight, visibleHeaderHeight, 0) + 16;
}

function openGiftsAccordion() {
  const picker = document.querySelector("[data-cart-picker-root]");
  const giftsPanel = picker?.querySelector('[data-tab-panel="gifts"]');
  if (!giftsPanel) return;

  giftsPanel.open = true;
  picker.saveAccordionState?.(giftsPanel);
}

function scrollToFreebiesSection() {
  const target =
    document.querySelector(".cart-samples-gifts-tabs__inner") ||
    document.getElementById("cart-samples-gifts-tabs");
  if (!target) return;

  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const scrollTop = Math.max(0, target.getBoundingClientRect().top + window.scrollY - getStickyHeaderOffset());

  if (!target.hasAttribute("tabindex")) {
    target.setAttribute("tabindex", "-1");
    target.addEventListener("blur", () => target.removeAttribute("tabindex"), { once: true });
  }

  target.focus({ preventScroll: true });
  window.scrollTo({
    top: scrollTop,
    behavior: prefersReducedMotion ? "auto" : "smooth",
  });
}

function continueToCheckout(checkoutButton) {
  if (!checkoutButton || cartFreebiesCheckoutSubmitting || checkoutButton.hasAttribute("disabled")) return;

  const formId = checkoutButton.getAttribute("form");
  const form = formId ? document.getElementById(formId) : null;
  if (!form) return;

  if (checkoutButton.closest("#CartDrawer")) window.hbCartDrawer?.resetCheckoutReminder?.();
  cartFreebiesCheckoutSubmitting = true;

  const cartFooter = checkoutButton.closest("#main-cart-footer");
  const checkoutButtons = cartFooter
    ? cartFooter.querySelectorAll("[data-cart-checkout-button]")
    : [checkoutButton];
  const processingText = window.cartStrings?.processing || "Processing...";

  checkoutButtons.forEach((btn) => {
    if (!btn.dataset.originalHtml) {
      btn.dataset.originalHtml = btn.innerHTML;
    }
    btn.setAttribute("disabled", "");
    btn.setAttribute("aria-disabled", "true");
    btn.classList.add("cart-checkout-button--loading", "cart-checkout-button--locked");
    btn.textContent = processingText;
  });

  if (!form.querySelector('input[name="checkout"]')) {
    const checkoutInput = document.createElement("input");
    checkoutInput.type = "hidden";
    checkoutInput.name = "checkout";
    checkoutInput.value = "";
    form.appendChild(checkoutInput);
  }
  form.submit();
}

/*
 * Skip leaves for checkout from a modal that stays open while the cart can still change under it: a gift
 * or sample add, or the gift check after one, holds window.hbCartBusy (assets/cart.js), and a Discount Kit
 * gift write holds the GWP lock (dk_gwp_updating). Checkout then opened on a cart that was still
 * settling. Wait for both to clear, for FREEBIES_SKIP_SETTLE_TIMEOUT_MS at most, then continue as the
 * checkout button would. A busy hold still open after the wait keeps checkout disabled, so Skip does not
 * go on: the shopper is told the cart is still updating (showFreebiesSkipBusyMessage) and taps checkout
 * again once it has settled, instead of the modal closing on nothing. The GWP lock alone does not stop
 * checkout once the wait is over, as waitForGiftCartIfBusy (assets/cart.js) goes on after its own hold:
 * the lock has no expiry, so a value a page left behind when it unloaded mid-write would otherwise block
 * checkout for the rest of the session. (Its only writer, assets/discountKit-gwp.js, is not loaded now.)
 * The trigger button can be re-rendered meanwhile (the cart redraw replaces the footer), so it is looked
 * up again by its form. Leaving the page ends the wait: a back/forward restore resumes its timers, and
 * must not then send the shopper on to checkout.
 */
const FREEBIES_SKIP_SETTLE_TIMEOUT_MS = 6000;
const FREEBIES_SKIP_SETTLE_POLL_MS = 100;
let cartFreebiesSkipSettling = false;
let cartFreebiesSkipGeneration = 0;
window.addEventListener("pagehide", () => {
  cartFreebiesSkipGeneration += 1;
});

function isCartBusyHoldOpen() {
  return window.hbCartBusy?.isBusy?.() === true;
}

function isCartWritePending() {
  if (isCartBusyHoldOpen()) return true;
  try {
    return sessionStorage.getItem("dk_gwp_updating") === "1";
  } catch {
    return false;
  }
}

// Written under the checkout buttons (main-cart-footer's data-cart-checkout-message, under the footer's
// button and in the mobile sticky bar, which is aria-hidden) and to the cart's alert region for screen
// readers. The next checkout tap clears it; a cart redraw replaces the footer, and the message with it.
function showFreebiesSkipBusyMessage() {
  const message =
    document.querySelector("[data-cart-promo-form]")?.dataset.busyMessage || window.cartStrings?.processing || "";
  if (!message) return;
  document.querySelectorAll("[data-cart-checkout-message]").forEach((element) => {
    element.textContent = message;
  });
  const cartAlert = document.getElementById("cart-errors");
  if (cartAlert) cartAlert.textContent = message;
}

function clearCartCheckoutMessage() {
  document.querySelectorAll("[data-cart-checkout-message]").forEach((element) => {
    element.textContent = "";
  });
}

async function continueToCheckoutWhenCartSettles(checkoutButton) {
  if (cartFreebiesSkipSettling || cartFreebiesCheckoutSubmitting) return;
  cartFreebiesSkipSettling = true;
  const generation = cartFreebiesSkipGeneration;
  try {
    const deadline = Date.now() + FREEBIES_SKIP_SETTLE_TIMEOUT_MS;
    while (isCartWritePending() && Date.now() < deadline && generation === cartFreebiesSkipGeneration) {
      await new Promise((resolve) => setTimeout(resolve, FREEBIES_SKIP_SETTLE_POLL_MS));
    }
  } finally {
    cartFreebiesSkipSettling = false;
  }
  if (generation !== cartFreebiesSkipGeneration) return;
  if (isCartBusyHoldOpen()) {
    showFreebiesSkipBusyMessage();
    return;
  }

  const formId = checkoutButton?.getAttribute("form");
  const button = checkoutButton?.isConnected
    ? checkoutButton
    : Array.from(document.querySelectorAll("#main-cart-footer [data-cart-checkout-button]")).find(
        (candidate) => candidate.getAttribute("form") === formId,
      );
  continueToCheckout(button);
}

function handleFreebiesModalClick(event) {
  if (event.target.closest("[data-cart-freebies-skip]")) {
    const triggerButton = cartFreebiesModalCheckoutButton;
    closeFreebiesModal({ restoreFocus: false });
    continueToCheckoutWhenCartSettles(triggerButton);
    return;
  }

  if (event.target.closest("[data-cart-freebies-select]")) {
    closeFreebiesModal({ restoreFocus: false });
    openGiftsAccordion();
    scrollToFreebiesSection();
    return;
  }

  if (event.target.closest("[data-cart-freebies-close]")) {
    closeFreebiesModal();
  }
}

function handleFreebiesModalKeydown(event) {
  if (event.key !== "Escape") return;
  event.preventDefault();
  closeFreebiesModal();
}

function initializeFreebiesModal() {
  const modal = getFreebiesModal();
  if (!modal || modal.closest("#CartDrawer") || modal.dataset.freebiesModalInitialized === "true") return;

  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }

  modal.addEventListener("click", handleFreebiesModalClick);
  modal.dataset.freebiesModalInitialized = "true";
}

// checkoutButton is the button the click matched: the in-flow one or the pinned bar's, on /cart or in the drawer.
function handleCheckoutButtonClick(event, checkoutButton) {
  if (!checkoutButton || checkoutButton.hasAttribute("disabled")) return;

  event.preventDefault();

  // A Skip already waiting for the cart to settle goes on to checkout itself.
  if (cartFreebiesCheckoutSubmitting || cartFreebiesSkipSettling) return;
  clearCartCheckoutMessage();

  // Once the drawer has offered the missing gift, its main checkout button becomes Checkout anyway.
  // Closing the picker keeps that choice while the drawer stays open; closing the drawer or a new page
  // resets it, so the reminder shows again.
  if (checkoutButton.closest("#CartDrawer") && window.hbCartDrawer?.isCheckoutReminderAcknowledged?.()) {
    continueToCheckoutWhenCartSettles(checkoutButton);
    return;
  }

  // Only a missing gift holds the tap back. A missing sample goes straight on: checkout adds one.
  if (hasUnselectedGift()) {
    // The drawer shows the reminder in its Free gifts & samples panel, on the gifts tab, and changes the bag's
    // checkout CTA. Without the drawer's script, wait for the cart to settle and go on.
    if (checkoutButton.closest("#CartDrawer")) {
      if (typeof window.hbCartDrawer?.openPanel === "function") {
        window.hbCartDrawer.openPanel("freebies", {
          intent: "checkout",
          tab: "gifts",
          opener: checkoutButton,
        });
      } else {
        continueToCheckoutWhenCartSettles(checkoutButton);
      }
      return;
    }
    openFreebiesModal(checkoutButton);
    return;
  }

  continueToCheckout(checkoutButton);
}

// Idempotent, and kept for callers that run it after a redraw: the checkout click needs no per-render binding
// (it is delegated below), so this only tidies and initializes the /cart freebies modal.
function initializeCartCheckoutButtons() {
  cleanupDuplicateFreebiesModals();
  cleanupStaleFreebiesModalState();
  initializeFreebiesModal();
}

window.initializeCartCheckoutButtons = initializeCartCheckoutButtons;
document.addEventListener("DOMContentLoaded", initializeCartCheckoutButtons);

// One listener for every checkout button of the cart footer, so a redraw of the footer (the cart page's
// swap, the drawer's section render) never leaves a new button unbound. The pinned bar's capture guard
// (assets/cart-mobile-sticky-checkout.js) still runs first and stops a click on a disabled bar.
if (!window.__hbCartCheckoutClickBound) {
  window.__hbCartCheckoutClickBound = true;
  document.addEventListener("click", (event) => {
    const checkoutButton =
      event.target instanceof Element ? event.target.closest("#main-cart-footer [data-cart-checkout-button]") : null;
    if (checkoutButton) handleCheckoutButtonClick(event, checkoutButton);
  });
}

// The drawer's script and its Free gifts & samples panel drive the same gate and the same continue path.
window.hbCartCheckout = {
  hasUnselectedFreebies,
  hasUnselectedGift,
  getFreebiesSelectionState,
  getMissingFreebieTypes,
  firstUnselectedType,
  continue: continueToCheckout,
  continueWhenSettled: continueToCheckoutWhenCartSettles,
};
})();

/*
 * cart.js only loads on the cart template and, in drawer mode, when the drawer first opens (layout/theme.liquid),
 * but product-form.js calls window.waitForGiftCartIfBusy?.() after every add to bag. Same logic as cart.js so
 * the GWP lock wait resumes if assets/discountKit-gwp.js is re-enabled; cart.js keeps its own copy on the cart
 * page.
 * Wrapped in an IIFE on purpose: cart.js declares GIFT_DRAWER_* and sleep at top level, and a second
 * top-level declaration of either would be a SyntaxError on the cart page.
 */
if (typeof window.waitForGiftCartIfBusy !== "function") {
  window.waitForGiftCartIfBusy = (() => {
    const ARM_MS = 1000;
    const HOLD_MS = 5000;
    const POLL_MS = 100;
    const GRACE_MS = 3000;
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const readLock = () => {
      try {
        return sessionStorage.getItem("dk_gwp_updating");
      } catch {
        return null;
      }
    };

    return async function waitForGiftCartIfBusy() {
      if (!window.__hbGwpLockWriterPresent && readLock() === null) return;

      const armDeadline = Date.now() + ARM_MS;
      while (readLock() === null && Date.now() < armDeadline) {
        await wait(POLL_MS);
      }

      if (readLock() !== "1") return;

      const holdDeadline = Date.now() + HOLD_MS;
      while (readLock() === "1" && Date.now() < holdDeadline) {
        await wait(POLL_MS);
      }

      await wait(GRACE_MS);
    };
  })();
}

/*
 * Another tab can change the bag (an add, a removal, a checkout). When this tab is shown again the bag is read
 * once, and if it is not the bag this tab is showing, the header count is redrawn and the bag on screen is
 * refreshed: the cart page, or the drawer (now if it is open, else on its next open). Nothing is read while the
 * tab stays in view, nothing is written, and nothing is redrawn when the bag did not change.
 *
 * Under load (Cyber Week) this used to read the cart twice per tab switch (once on hide, once on show), for guests
 * with no cart too. Now:
 *   - no cart cookie, no read (there is no bag to change);
 *   - at most one read per HB_CART_TAB_SYNC_MIN_INTERVAL_MS;
 *   - one read on show, compared with the bag this tab last saw: the last full cart it read or rendered
 *     (cartUpdate events that carry the cart), or else the count its header bubble shows (an add answers with its
 *     lines only), or before either the count the server rendered.
 */
const HB_CART_TAB_SYNC_MIN_INTERVAL_MS = 60000;
(() => {
  if (window.__hbCartTabSyncBound) return;
  window.__hbCartTabSyncBound = true;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  const isFullCart = (cart) =>
    Boolean(cart) && Array.isArray(cart.items) && typeof cart.item_count === "number" && typeof cart.total_price === "number";
  const signatureOf = (cart) =>
    isFullCart(cart)
      ? `${cart.item_count}|${cart.total_price}|${cart.items.map((item) => `${item.key}:${item.quantity}`).join(",")}`
      : null;
  const readCart = () =>
    (window.hbCartFetchWithTimeout || fetch)(`${root}cart.js`, {
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((cart) => (isFullCart(cart) ? cart : null))
      .catch(() => null);

  // The count in the header bubble (sections/cart-icon-bubble.liquid; Arabic pages may print Arabic-Indic digits).
  // 0 for a bubble with no count, null where there is no bubble (the /cart page) or a count of 100 or more.
  const shownCount = () => {
    const icon = document.getElementById("cart-icon-bubble");
    if (!icon) return null;
    const bubble = icon.querySelector(".cart-count-bubble");
    if (!bubble) return 0;
    const digits = (bubble.querySelector('[aria-hidden="true"]')?.textContent || "")
      .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660))
      .replace(/\D/g, "");
    return digits ? Number(digits) : null;
  };

  let knownSignature = null;
  let changedHere = false;
  let lastReadAt = 0;

  document.addEventListener("DOMContentLoaded", () => {
    if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined" || !PUB_SUB_EVENTS.cartUpdate) return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      knownSignature = signatureOf(event?.cartData);
      changedHere = true;
    });
  });

  const differsFromShown = (cart) => {
    if (knownSignature) return signatureOf(cart) !== knownSignature;
    const shown = shownCount();
    if (shown !== null) return cart.item_count !== shown;
    // No bubble to read: the server-rendered count holds until this tab changes the bag.
    return !changedHere && Number(cart.item_count) !== Number(window.hbInitialCartItemCount);
  };

  const refreshCartBubble = () => window.hbRefreshCartBubble?.();

  document.addEventListener("visibilitychange", async () => {
    if (document.visibilityState !== "visible" || !hbHasCartCookie()) return;
    if (Date.now() - lastReadAt < HB_CART_TAB_SYNC_MIN_INTERVAL_MS) return;
    // A change this tab is making itself is left to its own render (the shared busy registry, assets/cart.js).
    if (window.hbCartBusy?.isBusy?.() === true) return;
    lastReadAt = Date.now();
    const cart = await readCart();
    if (!cart) return;
    const changed = differsFromShown(cart);
    knownSignature = signatureOf(cart);
    if (!changed) return;

    try {
      await refreshCartBubble();
    } catch (error) {
      console.error("Cart bubble refresh after tab return failed:", error);
    }
    // A change started in this tab while the cart was being read wins over this refresh.
    if (window.hbCartBusy?.isBusy?.() === true) return;
    // The cart page, or the drawer's items (their refresh also runs the gift follow-ups); a closed
    // drawer only marks itself stale for its next open. The drawer hosts cart-items.cart-page, so both go
    // through the cart engine, which holds the busy lock and runs the post-render steps (as the drawer does).
    const drawerOpen = Boolean(window.hbCartDrawer && window.hbCartDrawer.isOpen && window.hbCartDrawer.isOpen());
    const cartItems = document.querySelector("cart-items.cart-page") || (drawerOpen ? document.querySelector("cart-drawer-items") : null);
    const engine = window.hbCartEngine;
    try {
      if (cartItems && cartItems.matches("cart-items.cart-page") && engine && typeof engine.refresh === "function") {
        await engine.refresh("tab-return");
      } else if (cartItems && typeof cartItems.updateCartPage === "function") {
        await cartItems.updateCartPage({ reason: "tab-return" });
      } else if (window.hbCartDrawer && typeof window.hbCartDrawer.refresh === "function") {
        await window.hbCartDrawer.refresh({ reason: "tab-return" });
      }
    } catch (error) {
      console.error("Cart refresh after tab return failed:", error);
    }
  });
})();
